import { useState } from 'react'
import { Trophy, Check, UserPlus, AlertTriangle, Shuffle } from 'lucide-react'
import { Card } from '@/shared/ui/card'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { Avatar } from '@/shared/ui/avatar'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { useAuthStore } from '@/core/auth/auth-store'
import { showToast } from '@/shared/ui/toast'
import { findMatch, getProfile, overrideMatch, type MockMatch } from '@/shared/mock/data'
import { getPlayerActions, setPlayerActions } from '@/shared/player-actions'
import { useRecordResult, type MatchView } from './use-matches'
import { useMyContacts, useSaveContact } from '@/features/contactos/use-contacts-real'
import { MembershipError } from './api/matches-repository'

const RESULT_BLOCK_MESSAGES: Record<string, string> = {
  'too-early': 'El partido aún no ha empezado.',
  'already-recorded': 'Otro jugador ya registró el resultado.',
  'not-player': 'Solo los jugadores del partido pueden registrar el resultado.',
}

// Post-match flow — the trust/competitive spine's entry point (roadmap #1/#2):
// record who won, flag no-shows, save the players you met. Result recording
// WRITES the match doc (transaction, spec/usersmatch/05); no-show flags and
// saves stay on the in-session store until reliability/contacts persist.
export function PostMatchPanel({ match }: { match: MockMatch | MatchView }) {
  const profile = getProfile()
  const user = useAuthStore((st) => st.user)
  const recordMutation = useRecordResult()
  // Bypass keeps the certified fixture path (override store survives
  // navigation). Real matches read the doc's result off the prop — the
  // mutation invalidates the query, so the prop refreshes on success.
  const stored = AUTH_BYPASS ? (findMatch(match.id)?.result ?? match.result) : match.result
  // Level movement from the calibration CF (competitive only, stamped
  // seconds after recording; visible on next data refresh — spec/05).
  const myDelta =
    !AUTH_BYPASS && user && 'resultLevelDeltas' in match
      ? ((match as { resultLevelDeltas?: Record<string, number> | null }).resultLevelDeltas?.[user.uid] ?? null)
      : null
  const [winner, setWinner] = useState<0 | 1 | null>(null)
  const recorded = !!stored
  const [, forceRender] = useState(0)
  // Join order ≠ on-court teams: the player can cycle the 3 possible 2v2
  // pairings before recording (recording a wrong result silently was an
  // audited defect, 2026-07-04).
  const [pairingLocal, setPairing] = useState(0)
  const pairing = stored ? stored.pairing : pairingLocal

  const p = match.players
  const PAIRINGS = [
    [[0, 1], [2, 3]],
    [[0, 2], [1, 3]],
    [[0, 3], [1, 2]],
  ] as const
  const pairIdx = PAIRINGS[pairing % 3]!
  const pairs = pairIdx.map((pair) => pair.map((i) => p[i]).filter(Boolean) as typeof p)
  const others = match.players.filter((pl) => pl.name !== profile.name)
  const myContacts = useMyContacts()
  const saveMutation = useSaveContact()
  const uidByName = new Map(
    match.players.map((pl, i) => [pl.name, (match as MatchView).playerUids?.[i] ?? null]),
  )
  const isSavedReal = (name: string) => {
    const uid = uidByName.get(name)
    return !!uid && (myContacts.data ?? []).some((e) => e.contact.contactUid === uid && e.contact.status === 'active')
  }
  // Real mode: flags are chosen BEFORE recording and ride the result write
  // (spec/06). Flaggable = seated app players other than me (guests have no
  // account to flag — the builder re-sanitizes anyway).
  const view = match as import('./use-matches').MatchView
  const flaggable =
    !AUTH_BYPASS && user
      ? match.players
          .map((pl, i) => ({ name: pl.name, uid: view.playerUids?.[i] ?? null, guest: (pl as { guest?: boolean }).guest }))
          .filter((pl) => pl.uid && pl.uid !== user.uid && !pl.guest)
      : []
  const [flagUids, setFlagUids] = useState<Set<string>>(new Set())
  const toggleFlag = (uid: string) => {
    navigator.vibrate?.(8)
    setFlagUids((prev) => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })
  }

  function cyclePairing() {
    navigator.vibrate?.(8)
    setPairing((n) => (n + 1) % 3)
    setWinner(null)
  }

  // Both write the SHARED store: a flagged no-show raises that player's
  // Ausencias everywhere; a saved player appears in Contactos (one fact,
  // one store).
  const toggleNoShow = (name: string) => {
    const flagged = (getPlayerActions(name).noShowsDelta ?? 0) > 0
    setPlayerActions(name, { noShowsDelta: flagged ? 0 : 1 })
    forceRender((n) => n + 1)
  }
  // One-way (contacts are a saved list, never a toggle here): tapping an
  // already-saved teammate must NOT silently un-save them.
  const saveContact = (name: string) => {
    if (!AUTH_BYPASS) {
      const uid = uidByName.get(name)
      if (!uid || isSavedReal(name)) return
      navigator.vibrate?.(10)
      saveMutation.mutate(uid)
      return
    }
    if (getPlayerActions(name).saved) return
    navigator.vibrate?.(10)
    setPlayerActions(name, { saved: true })
    forceRender((n) => n + 1)
  }

  function recordResult() {
    if (winner === null) return
    navigator.vibrate?.(12)
    if (AUTH_BYPASS) {
      overrideMatch(match.id, {
        result: { winners: (pairs[winner] ?? []).map((pl) => pl.name), pairing },
      })
      forceRender((n) => n + 1)
      return
    }
    if (!user) return
    recordMutation.mutate(
      {
        matchId: match.id,
        input: {
          pairingIndex: (pairing % 3) as 0 | 1 | 2,
          winnerPair: winner,
          recordedBy: user.uid,
          noShows: Array.from(flagUids),
          now: new Date(),
        },
      },
      {
        onError: (e) => {
          const reason = e instanceof MembershipError ? e.reason : ''
          showToast(RESULT_BLOCK_MESSAGES[reason] ?? 'No pudimos guardar el resultado. Inténtalo de nuevo.')
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)] mb-2">
          ¿Quién ganó?
        </p>
        <div className="grid grid-cols-2 gap-3">
          {pairs.map((pair, i) => {
            const active = stored
              ? pair.every((pl) => stored.winners.includes(pl.name)) && pair.length > 0
              : winner === i
            return (
              <button
                key={i}
                type="button"
                disabled={recorded}
                onClick={() => setWinner(i as 0 | 1)}
                className={`rounded-[var(--radius-card)] border p-3 flex flex-col items-center gap-2 transition-colors ${
                  active
                    ? 'border-[var(--accent)] bg-[var(--accent-wash)]'
                    : 'border-[var(--line)] hover:bg-[var(--line-soft)]'
                }`}
              >
                <div className="flex -space-x-2">
                  {pair.map((p) => (
                    <Avatar key={p.name} name={p.name} size={32} className="ring-2 ring-[var(--surface-card)]" />
                  ))}
                </div>
                <span className="text-[length:var(--fs-sm)] font-medium text-center">
                  {pair.map((p) => p.name.split(' ')[0]).join(' y ')}
                </span>
                {active && <Trophy size={16} className="text-[color:var(--accent)]" />}
              </button>
            )
          })}
        </div>
      </div>

      {!recorded && (
        <button
          type="button"
          onClick={cyclePairing}
          className="self-start flex items-center gap-1.5 min-h-[var(--tap)] text-[length:var(--fs-sm)] text-[color:var(--accent)]"
        >
          <Shuffle size={14} /> ¿Las parejas no eran así? Cambiar
        </button>
      )}

      {/* Real mode: flags ride the recording itself (spec/06) — chosen here,
          written atomically with the result. */}
      {!recorded && !AUTH_BYPASS && flaggable.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="flex items-center gap-2 text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)]">
            <AlertTriangle size={15} /> ¿Faltó alguien?
          </p>
          <div className="flex flex-wrap gap-2">
            {flaggable.map((pl) => {
              // `pl.uid!` is safe by construction: `flaggable` is filtered on
              // `pl.uid && …` two dozen lines up, so a seat with no uid (a
              // guest, or an unhydrated slot) never reaches this list — which
              // is exactly right, since there is no account to flag.
              const flagged = flagUids.has(pl.uid!)
              return (
                <Chip
                  key={pl.uid}
                  active={flagged}
                  onClick={() => toggleFlag(pl.uid!)}
                  className={flagged ? '!text-[color:var(--bad)] !border-[var(--bad)]' : ''}
                >
                  {flagged && <AlertTriangle size={13} />} {pl.name.split(' ')[0]}
                </Chip>
              )
            })}
          </div>
          <span className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
            Quien falte no puntúa este partido y suma una ausencia pública.
          </span>
        </div>
      )}

      {!recorded ? (
        <Button size="lg" disabled={winner === null || recordMutation.isPending} onClick={recordResult}>
          <Trophy size={18} /> {recordMutation.isPending ? 'Guardando…' : 'Registrar resultado'}
        </Button>
      ) : (
        <div className="um-enter flex flex-col gap-1">
          <p className="flex items-center gap-2 text-[length:var(--fs-sm)] text-[color:var(--ok)]">
            <Check size={15} /> Resultado guardado — ganaron{' '}
            {(stored ? stored.winners : (pairs[winner!] ?? []).map((pl) => pl.name))
              .map((n) => n.split(' ')[0])
              .join(' y ')}.
          </p>
          {myDelta != null && myDelta !== 0 && (
            <p
              className={`flex items-center gap-2 text-[length:var(--fs-sm)] font-medium ${
                myDelta > 0 ? 'text-[color:var(--ok)]' : 'text-[color:var(--fg-muted)]'
              }`}
            >
              <Trophy size={15} /> Tu nivel {myDelta > 0 ? 'sube' : 'baja'}{' '}
              {myDelta > 0 ? '+' : ''}
              {myDelta.toFixed(2).replace('.', ',').replace(/,?0+$/, '')} con este resultado.
            </p>
          )}
        </div>
      )}

      {recorded && (
        <Card className="um-enter">
          <div className="p-4 flex flex-col gap-4">
            {AUTH_BYPASS ? (
              <div className="flex flex-col gap-2">
                <p className="flex items-center gap-2 text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)]">
                  <AlertTriangle size={15} /> ¿Faltó alguien?
                </p>
                <div className="flex flex-wrap gap-2">
                  {others.map((p) => {
                    const flagged = (getPlayerActions(p.name).noShowsDelta ?? 0) > 0
                    return (
                      <Chip
                        key={p.name}
                        active={flagged}
                        onClick={() => toggleNoShow(p.name)}
                        className={flagged ? '!text-[color:var(--bad)] !border-[var(--bad)]' : ''}
                      >
                        {flagged && <AlertTriangle size={13} />} {p.name.split(' ')[0]}
                      </Chip>
                    )
                  })}
                </div>
                <span className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
                  Los no-shows cuentan en la fiabilidad pública de cada jugador.
                </span>
              </div>
            ) : (
              (view.resultNoShowNames?.length ?? 0) > 0 && (
                <p className="flex items-center gap-2 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                  <AlertTriangle size={15} className="text-[color:var(--bad)]" />
                  {/* Non-null by the length guard on this very branch. */}
                  Faltaron: {view.resultNoShowNames!.map((n) => n.split(' ')[0]).join(', ')}.
                </p>
              )
            )}

            <div className="flex flex-col gap-2">
              <p className="flex items-center gap-2 text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)]">
                <UserPlus size={15} /> Guarda a los que quieras repetir
              </p>
              <div className="flex flex-wrap gap-2">
                {others.map((p) => {
                  const isSaved = AUTH_BYPASS ? !!getPlayerActions(p.name).saved : isSavedReal(p.name)
                  // Saved reads as a STATE (muted + check), never as a
                  // tappable-looking active chip with no handler.
                  return isSaved ? (
                    <Chip key={p.name} className="opacity-60 pointer-events-none">
                      <Check size={13} /> {p.name.split(' ')[0]} · Guardado
                    </Chip>
                  ) : (
                    <Chip key={p.name} onClick={() => saveContact(p.name)}>
                      {p.name.split(' ')[0]}
                    </Chip>
                  )
                })}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
